import { Injectable, NgZone, computed, inject, signal } from "@angular/core";
import {
  CastOffProApp,
  type AppHandle,
  type AppOpts,
  type AppState,
  type AppStatePatch,
  type BoatControlsPatch,
  type BoatImpactEvent,
  type BoatTelemetry,
  type MooringPickupCandidate,
  type AnchorPick,
} from "../../../simulation-core";

/** Cleat keys that can be used to pick up a mooring with a boat hook. */
const PICKUP_CLEATS = ["bow", "stern_port", "stern_starboard"] as const;

/** Polling interval (ms) for refreshing pickup-candidate proximity. */
const PICKUP_POLL_MS = 150;

/**
 * Thin Angular wrapper over the imperative `CastOffProApp` lifecycle.
 *
 * Owns the canvas attachment and exposes the live handle as a signal so
 * components can react to readiness without polling. The simulation loop
 * runs OUTSIDE Angular's zone (zoneless app anyway, but explicit for safety
 * if anyone enables zone.js later).
 */
@Injectable({ providedIn: "root" })
export class SimService {
  private readonly zone = inject(NgZone);
  private app: CastOffProApp | null = null;

  readonly handle = signal<AppHandle | null>(null);
  readonly ready = computed(() => this.handle() !== null);

  /**
   * Live mapping of pickup-eligible cleats → closest mooring anchor in
   * boat-hook range, refreshed every {@link PICKUP_POLL_MS} ms while
   * the simulation is attached. Consumed by `CrewCommandsStore` to
   * (a) enable the "Podejmij mooring" button only when a buoy is in
   * reach and (b) cancel an in-flight pickup if the boat drifts away.
   */
  readonly pickupCandidates = signal<
    Record<string, MooringPickupCandidate | null>
  >({});

  private pickupTimer: ReturnType<typeof setInterval> | null = null;

  async attach(canvas: HTMLCanvasElement, opts: AppOpts = {}): Promise<void> {
    if (this.app) return;
    this.app = new CastOffProApp(canvas, {
      // Angular owns the overlay UI — disable the built-in HUD.
      enableHud: false,
      ...opts,
    });
    const h = await this.zone.runOutsideAngular(() => this.app!.start());
    this.handle.set(h);
    this.refreshPickupCandidates();
    this.pickupTimer = this.zone.runOutsideAngular(() =>
      setInterval(() => this.refreshPickupCandidates(), PICKUP_POLL_MS),
    );
    // Dev-only debug hook so the integrated test browser can poke the
    // sim handle directly (read pickup candidates, telemetry, teleport,
    // …). Stripped in prod builds.
    if (import.meta.env.DEV) {
      (window as unknown as { __sim?: unknown }).__sim = {
        handle: h,
        service: this,
      };
    }
  }

  detach(): void {
    if (this.pickupTimer !== null) {
      clearInterval(this.pickupTimer);
      this.pickupTimer = null;
    }
    this.pickupCandidates.set({});
    this.app?.dispose();
    this.app = null;
    this.handle.set(null);
  }

  patchState(patch: AppStatePatch): void {
    this.app?.setState(patch);
  }

  /**
   * Override environmental parameters used by Practice tier setup
   * (Calm / Moderate / Fresh / Gale). Thin wrapper over
   * {@link patchState} that accepts only the subset the gamification
   * layer cares about so callers can't accidentally clobber
   * unrelated state.
   */
  overrideEnvironment(patch: {
    readonly windSpeed?: number;
    readonly windDirectionDeg?: number;
    readonly maxWaveAmpScale?: number;
  }): void {
    const next: AppStatePatch = {};
    if (patch.windSpeed !== undefined) next.windSpeed = patch.windSpeed;
    if (patch.windDirectionDeg !== undefined) {
      next.windDirectionDeg = patch.windDirectionDeg;
    }
    if (patch.maxWaveAmpScale !== undefined) {
      next.maxWaveAmpScale = patch.maxWaveAmpScale;
    }
    this.app?.setState(next);
  }

  setBoatControls(patch: BoatControlsPatch): void {
    this.app?.setBoatControls(patch);
  }

  /** Read live telemetry (helm, throttle, heading, wind, …) from the boat. */
  boatTelemetry(): BoatTelemetry | null {
    return this.handle()?.boatTelemetry() ?? null;
  }

  /** Drain pending impact events (dock / ground hits) since last call. */
  consumeImpactEvents(): BoatImpactEvent[] {
    return this.handle()?.consumeImpactEvents() ?? [];
  }

  /**
   * Spawn a buoy at the given world XZ. Returns true when the buoy was
   * actually created (false on a duplicate id or before sim attach).
   * Used by gamification scenarios to seed course marks.
   */
  spawnBuoy(
    kind: string,
    posX: number,
    posZ: number,
    opts?: { id?: string; anchored?: boolean; headingY?: number },
  ): boolean {
    const h = this.handle();
    if (!h) return false;
    // `BuoyKind` is a string literal union internally; cast here keeps
    // callers in the app layer free of sim-internal types.
    return (
      h.spawnBuoy(
        kind as Parameters<typeof h.spawnBuoy>[0],
        posX,
        posZ,
        opts,
      ) !== null
    );
  }

  /** Snapshot of the live state, or null before attach. */
  liveState(): AppState | null {
    return this.handle()?.state ?? null;
  }

  /**
   * Direct (non-cached) probe of the closest mooring anchor in
   * boat-hook reach of the given cleat. Use this when an immediate
   * answer is needed mid-action; for reactive UI gating, prefer
   * subscribing to {@link pickupCandidates}.
   */
  probePickupCandidate(cleatName: string): MooringPickupCandidate | null {
    return this.handle()?.getMooringPickupCandidate(cleatName) ?? null;
  }

  /** Player boat freeboard (m); used to scale crew action timings. */
  playerFreeboard(): number {
    return this.handle()?.getPlayerFreeboard() ?? 0.6;
  }

  /** Player boat anchor equipment and limits. */
  playerAnchorEquipment(): {
    hasDepthSounder: boolean;
    anchorChainMaxLength: number;
    anchorRodeType: "rope" | "chain" | "mixed";
    anchorRodeMaxLength: number;
    anchorRodeWeightKgPerM: number;
  } {
    return (
      this.handle()?.getPlayerAnchorEquipment() ?? {
        hasDepthSounder: false,
        anchorChainMaxLength: 60,
        anchorRodeType: "rope",
        anchorRodeMaxLength: 60,
        anchorRodeWeightKgPerM: 0.08,
      }
    );
  }

  /** True if the player boat's mast can be lowered on a tabernacle. */
  playerHasFoldableMast(): boolean {
    return this.handle()?.getPlayerHasFoldableMast() ?? false;
  }

  /** Drive the visual mast rotation (rad). 0 = up, π/2 = laid aft. */
  setPlayerMastAngleTarget(angleRad: number): void {
    this.handle()?.setPlayerMastAngleTarget(angleRad);
  }

  setPlayerBoomDetached(detached: boolean): void {
    this.handle()?.setPlayerBoomDetached(detached);
  }

  /** Available terrain presets discovered at startup. */
  listTerrainPresets(): Array<{ id: string; name: string }> {
    return this.handle()?.listTerrainPresets() ?? [];
  }

  /** Available player boat presets discovered at startup. */
  listBoatChoices(): Array<{ id: string; name: string }> {
    return this.handle()?.listBoatChoices() ?? [];
  }

  /** Trigger an immediate bird flyover (small V-flock) around the player. */
  releaseBirds(): void {
    this.handle()?.releaseBirds();
  }

  /**
   * Cast a pick ray through the given normalised device coordinates
   * and return the closest mooring anchor (bollard or buoy), or null
   * if no anchor is under the cursor.
   */
  pickAnchorAtNdc(ndcX: number, ndcY: number): AnchorPick | null {
    return this.handle()?.pickAnchorAtNdc(ndcX, ndcY) ?? null;
  }

  /**
   * Current world distance (m) between a cleat's fairlead and its
   * bound anchor. Returns null when the line is loose / unbound.
   */
  fairleadAnchorDistance(cleatName: string): number | null {
    return this.handle()?.getFairleadAnchorDistance(cleatName) ?? null;
  }

  private refreshPickupCandidates(): void {
    const h = this.handle();
    if (!h) return;
    const next: Record<string, MooringPickupCandidate | null> = {};
    for (const c of PICKUP_CLEATS) {
      next[c] = h.getMooringPickupCandidate(c);
    }
    // Diff-skip: poll runs every 150ms but ~all ticks are no-ops
    // (boat far from any anchor). Only notify consumers on real change
    // to avoid downstream change-detection churn.
    const prev = this.pickupCandidates();
    let changed = false;
    for (const c of PICKUP_CLEATS) {
      const a = prev[c] ?? null;
      const b = next[c] ?? null;
      if (a === b) continue;
      if (!a || !b || a.anchorId !== b.anchorId || a.distance !== b.distance) {
        changed = true;
        break;
      }
    }
    if (changed) this.pickupCandidates.set(next);
  }
}
